// CreateDevice.js
import React, { useState, useCallback, useEffect, useRef } from "react";
import SubTitle from "../common/SubTitle";
import { Box, Typography, Alert, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import DeviceDetails from "./DeviceDetails";
import SimDetails from "./SimDetails";
import InstallationDetails from "./InstallationDetails";
import ExecutiveDetails from "./ExecutiveDetails";
import InstalledPhotos from "./InstalledPhotos";
import {
  createTrackingDevice,
  updateTrackingDevice,
  getTrackingDeviceById,
  formatDeviceData,
  validateDevice,
  isValidEmail,
  isValidIMEI,
  isValidPhoneNumber,
} from "./deviceApi";

const INSTALLATION_DETAIL_FIELDS = new Set([
  "installationDate",
  "assignmentType",
  "organizationId",
  "organizationName",
  "city",
  "site",
  "vehicle",
  "stop",
  "stopId",
  "stopName",
  "vehicleRegistrationNumber",
  "vehicleModel",
  "executiveFullName",
  "executivePhoneNumber",
  "executiveEmail",
  "remarks",
  "status",
]);

const specificationsToRows = (specifications) => {
  if (typeof specifications === "string") {
    try {
      return specificationsToRows(JSON.parse(specifications));
    } catch {
      return [{ id: "spec-1", name: "", value: "" }];
    }
  }
  if (!specifications || typeof specifications !== "object") {
    return [{ id: "spec-1", name: "", value: "" }];
  }
  if (Array.isArray(specifications)) {
    const rows = specifications
      .filter((specification) => specification && typeof specification === "object")
      .map((specification, index) => ({
        id: specification.id ? String(specification.id) : `spec-${index + 1}`,
        name: specification.name || "",
        value:
          specification.value == null
            ? ""
            : typeof specification.value === "object"
              ? JSON.stringify(specification.value)
              : String(specification.value),
      }));
    return rows.length > 0 ? rows : [{ id: "spec-1", name: "", value: "" }];
  }
  const rows = Object.entries(specifications).map(([name, value], index) => ({
    id: `spec-${index + 1}`,
    name,
    value:
      value == null
        ? ""
        : typeof value === "object"
          ? JSON.stringify(value)
          : String(value),
  }));
  return rows.length > 0 ? rows : [{ id: "spec-1", name: "", value: "" }];
};

const toDateInputValue = (dateValue) =>
  dateValue ? String(dateValue).split("T")[0] : "";

const normalizeSimStatus = (status) => {
  const normalizedStatus = String(status || "INACTIVE").trim().toUpperCase();
  return normalizedStatus === "ACTIVE" ? "ACTIVE" : "INACTIVE";
};

export default function CreateDevice({
  embedded = false,
  embeddedDeviceId,
  onCancel,
  onComplete,
  onDelete,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deviceId: routeDeviceId } = useParams();
  const deviceId = embeddedDeviceId || routeDeviceId;
  const isEditMode = Boolean(deviceId) && !isNaN(Number(deviceId));

  // Add this ref at the top with other refs
const isLoadingDataRef = useRef(false);

  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [existingSimCardId, setExistingSimCardId] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [ownerOrganizations, setOwnerOrganizations] = useState([]);
  const getCurrentUserState = () => {
    try {
      return JSON.parse(localStorage.getItem("userState") || "{}");
    } catch {
      return {};
    }
  };
  const currentUserState = getCurrentUserState();
  const currentUser = currentUserState?.user || {};
  const currentOrganization = currentUserState?.organization || {};
  const currentOrganizationId =
    currentUser?.organizationId || currentOrganization?.id || "";
  const currentOrganizationName = currentOrganization?.name || "";
  const roleName = String(
    currentUser?.role?.name ||
      currentUser?.role ||
      currentUserState?.role?.name ||
      currentUserState?.role ||
      "",
  ).toLowerCase();
  const roleId =
    currentUser?.roleId ||
    currentUserState?.roleId ||
    currentUser?.role?.id ||
    currentUserState?.role?.id;
  const isSuperAdmin =
    currentUser?.email === "sales@routeeye.io" ||
    Number(roleId) === 3 ||
    roleName.includes("superadmin") ||
    roleName.includes("super admin");
  const isOrganizationAdmin = !isSuperAdmin && Boolean(currentOrganizationId);

  const [formData, setFormData] = useState({
    deviceId: "",
    deviceType: "",
    sensorType: "",
    unit: "",
    minValue: "",
    maxValue: "",
    imeiNumber: "",
    model: "",
    vendorBrand: "",
    ownedBy: "Irisidea",
    ownedByType: "Irisidea",
    ownedByOrganizationId: "",
    ownedByOther: "",
    workingCondition: "NOT_WORKING",
    status: "PENDING",
    deviceImage: null,
    deviceImagePreview: null,
    installationDate: "",
    assignmentType: "VEHICLE",
    organizationId: "",
    organizationName: "",
    city: "",
    site: "",
    vehicle: null,
    stop: null,
    stopId: "",
    stopName: "",
    vehicleRegistrationNumber: "",
    vehicleModel: "",
    executiveFullName: "",
    executivePhoneNumber: "",
    executiveEmail: "",
    remarks: "",
    simCard: {
      simImei: "",
      mobileNumber: "",
      nextRechargeDate: "",
      provider: "",
      ownedBy: "",
      simActivationDate: "",
      simStatus: "INACTIVE",
    },
    specifications: [{ id: "spec-1", name: "", value: "" }],
  });

  // Separate state for installation photos - initialize with proper structure
  const [installedPhotos, setInstalledPhotos] = useState([
    {
      id: "photo-1",
      file: null,
      fileName: "",
      preview: null,
      isExisting: false,
      isUploaded: false,
      needsReplacement: false,
      serverId: null,
    },
  ]);
  const [errors, setErrors] = useState({});

  const isIrisideaOwnedDevice =
    String(formData.ownedBy || "").toLowerCase() === "irisidea" ||
    formData.ownedByType === "Irisidea";
  const isReadOnlyForOrgAdmin =
    isOrganizationAdmin && isEditMode && isIrisideaOwnedDevice;
  const isInstallationReadOnlyForOrgAdmin = isOrganizationAdmin && isEditMode;
  // Device ownership is fixed to Irisidea. Organization selection is handled
  // separately in installation/attached-to details.
  const lockOwnerToOrganization = false;
  const canSubmitDevice = true;

  useEffect(() => {
    const fetchOwnerOrganizations = async () => {
      try {
        const response = await axios.get(`${config.baseURL}/organization`);
        setOwnerOrganizations(response?.data?.organizations || []);
      } catch (error) {
        console.error("Error fetching customer organizations:", error);
        setOwnerOrganizations([]);
      }
    };

    fetchOwnerOrganizations();
  }, []);

  const clearInstallationAssignmentFields = useCallback(
    (baseFields = {}) => ({
      ...baseFields,
      site: "",
      vehicle: null,
      stop: null,
      stopId: "",
      stopName: "",
      vehicleRegistrationNumber: "",
      vehicleModel: "",
    }),
    [],
  );

  useEffect(() => {
    if (!isOrganizationAdmin || isEditMode || !currentOrganizationId) return;
    const ownerOrganization = ownerOrganizations.find(
      (org) => String(org.id) === String(currentOrganizationId),
    );
    const ownerOrganizationName =
      ownerOrganization?.name || currentOrganizationName;
    const ownerOrganizationCity =
      ownerOrganization?.city || currentOrganization.city || "";
    const lockedOrganization = ownerOrganization || {
      id: currentOrganizationId,
      name: ownerOrganizationName,
      city: ownerOrganizationCity,
    };

    setSelectedOrganization((prev) =>
      String(prev?.id || "") === String(lockedOrganization.id)
        ? prev
        : lockedOrganization,
    );

    setFormData((prev) => {
      if (
        prev.ownedByType === "Customer" &&
        String(prev.ownedByOrganizationId || "") ===
          String(currentOrganizationId) &&
        prev.ownedBy === ownerOrganizationName
      ) {
        return prev;
      }

      return clearInstallationAssignmentFields({
        ...prev,
        ownedBy: "Irisidea",
        ownedByType: "Irisidea",
        ownedByOrganizationId: "",
        ownedByOther: "",
        organizationId: String(currentOrganizationId),
        organizationName: ownerOrganizationName,
        city: ownerOrganizationCity,
      });
    });
  }, [
    clearInstallationAssignmentFields,
    currentOrganization.city,
    currentOrganizationId,
    currentOrganizationName,
    isEditMode,
    isOrganizationAdmin,
    ownerOrganizations,
  ]);

  useEffect(() => {
    if (
      formData.ownedByType !== "Customer" ||
      !formData.ownedByOrganizationId ||
      ownerOrganizations.length === 0
    ) {
      return;
    }

    const ownerOrganization = ownerOrganizations.find(
      (org) => String(org.id) === String(formData.ownedByOrganizationId),
    );

    if (!ownerOrganization) return;

    setSelectedOrganization((prev) =>
      String(prev?.id || "") === String(ownerOrganization.id)
        ? prev
        : ownerOrganization,
    );

    setFormData((prev) => {
      if (
        prev.organizationName === ownerOrganization.name &&
        prev.city === (ownerOrganization.city || "")
      ) {
        return prev;
      }

      return clearInstallationAssignmentFields({
        ...prev,
        organizationName: ownerOrganization.name,
        city: ownerOrganization.city || "",
      });
    });
  }, [
    clearInstallationAssignmentFields,
    formData.ownedByOrganizationId,
    formData.ownedByType,
    ownerOrganizations,
  ]);

  // Load device data if in edit mode
  useEffect(() => {
    if (isEditMode && deviceId) {
      loadDeviceData();
    }
  }, [isEditMode, deviceId]);

  const loadDeviceData = async () => {
    try {
      setIsLoading(true);
      const response = await getTrackingDeviceById(deviceId);
      const deviceData = response.data; // Access the data property from response

      // Store existing simCard ID
      if (deviceData.SimCard?.id) {
        // Changed from simCard to SimCard
        setExistingSimCardId(deviceData.SimCard.id);
      }

      // Set selected organization directly from assigned vehicle/stop data
      const assignedOrganization =
        deviceData.Vehicle?.organization ||
        deviceData.Stop?.organization ||
        (deviceData.organizationId || deviceData.organizationName
          ? {
              id: deviceData.organizationId,
              name: deviceData.organizationName,
              city: deviceData.city,
            }
          : null);

      if (
        isOrganizationAdmin &&
        (!assignedOrganization?.id ||
          String(assignedOrganization.id) !== String(currentOrganizationId))
      ) {
        setSubmitError(t("You can only access devices from your organization"));
        return;
      }

      if (assignedOrganization) {
        setSelectedOrganization({
          id: assignedOrganization.id,
          name: assignedOrganization.name,
          city: assignedOrganization.city,
        });
      }

      const savedOwnedBy = deviceData.ownedBy || "Irisidea";
      const isCustomerOwned =
        savedOwnedBy && String(savedOwnedBy).toLowerCase() !== "irisidea";

      setFormData({
        id: deviceData.id,
        deviceId: deviceData.deviceId,
        deviceType: deviceData.deviceType || "",
        sensorType: deviceData.sensorType || "",
        unit: deviceData.unit || "",
        minValue: deviceData.minValue ?? "",
        maxValue: deviceData.maxValue ?? "",
        imeiNumber: deviceData.imeiNumber || "",
        model: deviceData.model || "",
        vendorBrand: deviceData.vendorBrand || "",
        ownedBy: savedOwnedBy,
        ownedByType: isCustomerOwned ? "Customer" : "Irisidea",
        ownedByOrganizationId: isCustomerOwned
          ? deviceData.organizationId ||
            deviceData.Vehicle?.organization?.id ||
            deviceData.Stop?.organization?.id ||
            ""
          : "",
        ownedByOther: "",
        workingCondition: deviceData.workingCondition || "",
        status: deviceData.status || "",
        deviceImage: null,
        deviceImagePreview: deviceData.deviceImageUrl || null,
        installationDate: deviceData.installationDate?.split("T")[0] || "",
        assignmentType:
          deviceData.assignmentType === "OTHER"
            ? "VEHICLE"
            : deviceData.assignmentType || (deviceData.Stop ? "STOP" : "VEHICLE"),
        // Organization details from vehicle
        organizationName:
          deviceData.Vehicle?.organization?.name ||
          deviceData.Stop?.organization?.name ||
          deviceData.organizationName ||
          "",
        organizationId:
          deviceData.organizationId ||
          deviceData.Vehicle?.organization?.id ||
          deviceData.Stop?.organization?.id ||
          "",
        city:
          deviceData.Vehicle?.organization?.city ||
          deviceData.Stop?.organization?.city ||
          deviceData.city ||
          "",
        site: deviceData.Vehicle?.siteId || deviceData.Stop?.siteId || "",
        // Vehicle details
        vehicle: deviceData.Vehicle
          ? {
              id: deviceData.Vehicle.id,
              name: deviceData.Vehicle.name,
              registrationNumber: deviceData.Vehicle.registrationNumber,
              model: deviceData.Vehicle.model,
            }
          : null,
        stop: deviceData.Stop
          ? {
              id: deviceData.Stop.id,
              stopId: deviceData.Stop.stopId,
              name: deviceData.Stop.name,
              latitude: deviceData.Stop.latitude,
              longitude: deviceData.Stop.longitude,
              landmark: deviceData.Stop.landmark,
              siteId: deviceData.Stop.siteId,
              site: deviceData.Stop.site,
            }
          : null,
        stopId: deviceData.Stop?.stopId || deviceData.stopId || "",
        stopName: deviceData.Stop?.name || deviceData.stopName || "",
        vehicleRegistrationNumber: deviceData.Vehicle?.registrationNumber || "",
        vehicleModel: deviceData.Vehicle?.model || "",
        // Executive details
        executiveFullName: deviceData.executiveFullName || "",
        executivePhoneNumber: deviceData.executivePhoneNumber || "",
        executiveEmail: deviceData.executiveEmail || "",
        remarks: deviceData.remarks || "",
        // SIM card details
        simCard: {
          simImei: deviceData.SimCard?.imeiNumber || "", // Changed from simCard to SimCard
          mobileNumber: deviceData.SimCard?.simNumber || "",
          nextRechargeDate: toDateInputValue(deviceData.SimCard?.planExpiry),
          provider: deviceData.SimCard?.provider || "",
          ownedBy: deviceData.SimCard?.ownedBy || "",
          simActivationDate: toDateInputValue(
            deviceData.SimCard?.simActivationDate ||
              deviceData.SimCard?.activationDate ||
              deviceData.SimCard?.activatedAt,
          ),
          simStatus: normalizeSimStatus(
            deviceData.SimCard?.status || deviceData.SimCard?.simStatus,
          ),
        },
        specifications: specificationsToRows(deviceData.specifications), // ← add this
      });

      // Handle photos if they exist
      if (deviceData.Document && deviceData.Document.length > 0) {
        // Changed from photos to Document
        const photoData = deviceData.Document.map((photo, index) => ({
          id: `photo-${photo.photoNumber || index + 1}`,
          photoNumber: photo.photoNumber || index + 1,
          file: null,
          fileName: photo.location ? photo.location.split("/").pop() : "",
          preview: photo.url,
          isExisting: true,
          isUploaded: true,
          serverId: photo.id,
          needsReplacement: false,
          createdAt: photo.createdAt,
          updatedAt: photo.updatedAt,
        }));
        setInstalledPhotos(photoData);
      }
    } catch (error) {
      console.error("Error loading device data:", error);
      setSubmitError(t("Failed to load device data. Please try again."));
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        isLoadingDataRef.current = false;  // ✅ ADD
      }, 500);
    }
  };

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      if (
        formData.deviceImagePreview &&
        formData.deviceImagePreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(formData.deviceImagePreview);
      }
      // Cleanup photo previews
      installedPhotos.forEach((photo) => {
        if (photo.preview && photo.preview.startsWith("blob:")) {
          URL.revokeObjectURL(photo.preview);
        }
      });
    };
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

    // ONLY validate required fields for basic form validation
    if (!formData.deviceId?.trim()) {
      newErrors.deviceId = t("Device ID is required");
    }

    if (!formData.status?.trim()) {
      newErrors.status = t("Status is required");
    }

    if (!formData.workingCondition?.trim()) {
      newErrors.workingCondition = t("Working Condition is required");
    }

    if (lockOwnerToOrganization) {
      const isCurrentOrganizationOwner =
        formData.ownedByType === "Customer" &&
        String(formData.ownedByOrganizationId || "") ===
          String(currentOrganizationId);

      if (!isCurrentOrganizationOwner) {
        newErrors.ownedBy = t(
          "Organization admin can only use their organization as owner",
        );
      }
    }

    if (
      formData.ownedByType === "Customer" &&
      !formData.ownedByOrganizationId
    ) {
      newErrors.ownedBy = t("Please select customer organization");
    }

    // Device IMEI validation
    if (
      formData.imeiNumber?.trim() &&
      !isValidIMEI(formData.imeiNumber.trim())
    ) {
      newErrors.imeiNumber = t("IMEI number must be numeric");
    }

    if (formData.minValue !== "" && Number.isNaN(Number(formData.minValue))) {
      newErrors.minValue = t("Min threshold must be numeric");
    }

    if (formData.maxValue !== "" && Number.isNaN(Number(formData.maxValue))) {
      newErrors.maxValue = t("Max threshold must be numeric");
    }

    if (
      formData.minValue !== "" &&
      formData.maxValue !== "" &&
      !Number.isNaN(Number(formData.minValue)) &&
      !Number.isNaN(Number(formData.maxValue)) &&
      Number(formData.minValue) > Number(formData.maxValue)
    ) {
      newErrors.maxValue = t("Max threshold must be greater than or equal to min threshold");
    }

    // SIM IMEI validation
    if (
      formData.simCard?.simImei?.trim() &&
      !isValidIMEI(formData.simCard.simImei.trim())
    ) {
      newErrors.simImei = t("SIM IMEI must be numeric");
    }

    // Phone number validation
    if (
      formData.executivePhoneNumber?.trim() &&
      !isValidPhoneNumber(formData.executivePhoneNumber.trim())
    ) {
      newErrors.executivePhoneNumber = t("Please enter a valid phone number");
    }

    // Mobile number validation (SIM card)
    if (
      formData.simCard?.mobileNumber?.trim() &&
      !isValidPhoneNumber(formData.simCard.mobileNumber.trim())
    ) {
      newErrors.mobileNumber = t("Please enter a valid mobile number");
    }

    // Email validation
    if (
      formData.executiveEmail?.trim() &&
      !isValidEmail(formData.executiveEmail.trim())
    ) {
      newErrors.executiveEmail = t("Please enter a valid email address");
    }

    // Date validations
    // Installation date should be past or present (not future)
    if (formData.installationDate) {
      const installationDate = new Date(formData.installationDate);
      installationDate.setHours(0, 0, 0, 0);

      if (installationDate > today) {
        newErrors.installationDate = t(
          "Installation date cannot be in the future",
        );
      }
    }

    if (formData.simCard?.simActivationDate) {
      const simActivationDate = new Date(formData.simCard.simActivationDate);
      simActivationDate.setHours(0, 0, 0, 0);

      if (simActivationDate > today) {
        newErrors.simActivationDate = t(
          "SIM activation date cannot be in the future",
        );
      }
    }

    // Next recharge date should be future (not past)
    if (formData.simCard?.nextRechargeDate) {
      const rechargeDate = new Date(formData.simCard.nextRechargeDate);
      rechargeDate.setHours(0, 0, 0, 0);

      if (rechargeDate < today) {
        newErrors.nextRechargeDate = t(
          "Next recharge date cannot be in the past",
        );
      }
    }

    // Only validate optional fields if they have values (and only basic format checks)
    if (formData.deviceImage && formData.deviceImage instanceof File) {
      if (formData.deviceImage.size > 3 * 1024 * 1024) {
        newErrors.deviceImage = t("Image size should be less than 3MB");
      }
      if (!formData.deviceImage.type.startsWith("image/")) {
        newErrors.deviceImage = t("Please select a valid image file");
      }
    }

    // Update errors state immediately so they can be displayed
    setErrors(newErrors);

    // Return validation result
    const isValid = Object.keys(newErrors).length === 0;

    // If there are errors, ensure they are properly captured for the submit handler
    if (!isValid) {
      console.log("Validation errors:", newErrors);
    }

    return isValid;
  }, [
    formData.deviceId,
    formData.status,
    formData.workingCondition,
    formData.ownedBy,
    formData.ownedByType,
    formData.ownedByOrganizationId,
    formData.imeiNumber,
    formData.minValue,
    formData.maxValue,
    formData.simCard?.simImei,
    formData.executivePhoneNumber,
    formData.simCard?.mobileNumber,
    formData.executiveEmail,
    formData.installationDate,
    formData.simCard?.simActivationDate,
    formData.simCard?.nextRechargeDate,
    formData.deviceImage,
    currentOrganizationId,
    lockOwnerToOrganization,
    t,
  ]);
  const handleInputChange = useCallback(
    (e, field) => {
      const value = e.target.value;
      if (
        isOrganizationAdmin &&
        field === "organizationId" &&
        String(value || "") !== String(currentOrganizationId || "")
      ) {
        return;
      }
      if (
        isOrganizationAdmin &&
        field === "organizationName" &&
        String(value || "") !== String(currentOrganizationName || "")
      ) {
        return;
      }
      if (
        isInstallationReadOnlyForOrgAdmin &&
        INSTALLATION_DETAIL_FIELDS.has(field)
      ) {
        return;
      }

      const ownerOrganization =
        field === "ownedByOrganizationId"
          ? ownerOrganizations.find((org) => String(org.id) === String(value))
          : null;

      if (field === "ownedByOrganizationId") {
        setSelectedOrganization(ownerOrganization || null);
      } else if (field === "ownedByType" && value !== "Customer") {
        setSelectedOrganization(null);
      }

      // Handle nested simCard fields
      if (field.startsWith("simCard.")) {
        const simCardField = field.split(".")[1];
        setFormData((prev) => ({
          ...prev,
          simCard: {
            ...prev.simCard,
            [simCardField]: value,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...(() => {
            const nextData = {
              ...prev,
              [field]: value,
            };

            if (field === "ownedByOrganizationId") {
              return clearInstallationAssignmentFields({
                ...nextData,
                organizationId: ownerOrganization?.id || "",
                organizationName: ownerOrganization?.name || "",
                city: ownerOrganization?.city || "",
              });
            }

            if (field === "ownedByType" && value !== "Customer") {
              return clearInstallationAssignmentFields({
                ...nextData,
                organizationId: "",
                organizationName: "",
                city: "",
              });
            }

            return nextData;
          })(),
          ...(!isEditMode && ["deviceId", "deviceType"].includes(field)
            ? {
                assignmentType: "VEHICLE",
                organizationId: "",
                organizationName: "",
                city: "",
                site: "",
                vehicle: null,
                stop: null,
                stopId: "",
                stopName: "",
                vehicleRegistrationNumber: "",
                vehicleModel: "",
                remarks: "",
              }
            : {}),
          ...(field === "deviceType" && value !== "SENSOR"
            ? { sensorType: "", unit: "", minValue: "", maxValue: "" }
            : {}),
        }));
      }

      // Clear errors for the field
      const errorField = field.startsWith("simCard.")
        ? field.split(".")[1]
        : field;
      const relatedErrorFields = [errorField];
      if (["ownedByType", "ownedByOrganizationId", "ownedBy"].includes(field)) {
        relatedErrorFields.push("ownedBy");
      }

      if (relatedErrorFields.some((fieldName) => errors[fieldName])) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          relatedErrorFields.forEach((fieldName) => {
            delete newErrors[fieldName];
          });
          return newErrors;
        });
      }
    },
    [
      clearInstallationAssignmentFields,
      currentOrganizationId,
      currentOrganizationName,
      errors,
      isEditMode,
      isInstallationReadOnlyForOrgAdmin,
      isOrganizationAdmin,
      ownerOrganizations,
    ],
  );

  const handleImageUpload = useCallback(
    (e) => {
      const file = e.target.files[0];
      if (file) {
        // Validate file
        if (file.size > 10 * 1024 * 1024) {
          alert(t("Image size should be less than 10MB"));
          return;
        }

        if (!file.type.startsWith("image/")) {
          alert(t("Please select a valid image file"));
          return;
        }

        // Clean up previous preview if it was a blob URL
        if (
          formData.deviceImagePreview &&
          formData.deviceImagePreview.startsWith("blob:")
        ) {
          URL.revokeObjectURL(formData.deviceImagePreview);
        }

        const preview = URL.createObjectURL(file);

        setFormData((prev) => ({
          ...prev,
          deviceImage: file,
          deviceImagePreview: preview,
        }));

        if (errors.deviceImage) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.deviceImage;
            return newErrors;
          });
        }
      }
    },
    [formData.deviceImagePreview, errors, t],
  );

  const handleRemoveImage = useCallback(async () => {
    // If it's an existing image (from server)
    if (
      formData.deviceImagePreview &&
      !formData.deviceImagePreview.startsWith("blob:")
    ) {
      try {
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: {
            name: "DeviceImage",
            associateEntityId: deviceId,
            associatedWith: "Device",
            type: "profile",
          },
          withCredentials: true,
        });
      } catch (error) {
        console.error("Error deleting device image:", error);
        setSubmitError(t("Failed to delete device image"));
        return;
      }
    }

    // Clean up blob URL if exists
    if (
      formData.deviceImagePreview &&
      formData.deviceImagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(formData.deviceImagePreview);
    }

    setFormData((prev) => ({
      ...prev,
      deviceImage: null,
      deviceImagePreview: null,
    }));

    // Clear any image-related errors
    if (errors.deviceImage) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.deviceImage;
        return newErrors;
      });
    }
  }, [formData.deviceImagePreview, deviceId, errors, t]);

  const handleSubmit = async () => {
    setSubmitError("");
    setSubmitSuccess("");

    if (!canSubmitDevice) {
      setSubmitError(t("Irisidea owned devices can only be updated by superadmin"));
      return;
    }

    // Run form validation first
    if (!validateForm()) {
      // Get all validation errors and display them
      const errorMessages = Object.values(errors).filter((error) => error);
      if (errorMessages.length > 0) {
        setSubmitError(errorMessages.join(". "));
      } else {
        setSubmitError(t("Please fix the errors"));
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Use the updated formatDeviceData function with simCard ID for updates
      const deviceData = formatDeviceData(
        formData,
        isEditMode,
        existingSimCardId,
      );
      const validation = validateDevice(deviceData);

      if (!validation.isValid) {
        // Show specific validation errors from deviceApi
        setSubmitError(validation.errors.join(". "));
        setIsSubmitting(false);
        return;
      }

      let response, currentDeviceId;

      if (isEditMode) {
        // Update existing device
        deviceData.id = deviceId;
        response = await updateTrackingDevice(deviceData);
        currentDeviceId = deviceId;
        console.log("Device updated with ID:", currentDeviceId);
      } else {
        // Create new device
        response = await createTrackingDevice(deviceData);
        currentDeviceId =
          response?.data?.device?.id ||
          response?.data?.id ||
          response?.device?.id ||
          response?.id;
        console.log("Device created with ID:", currentDeviceId);
      }

      // Upload device image if provided
      if (
        formData.deviceImage &&
        formData.deviceImage instanceof File &&
        currentDeviceId
      ) {
        try {
          // Wait a moment to ensure device is available in backend
          await new Promise((resolve) => setTimeout(resolve, 500));

          const imageFormData = new FormData();
          imageFormData.append("image", formData.deviceImage);

          const imageResponse = await axios.post(
            `${config.baseURL}/upload/image?name=${encodeURIComponent(
              "DeviceImage",
            )}&associateEntityId=${currentDeviceId}&associatedWith=Device&type=profile`,
            imageFormData,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
            },
          );

          console.log(
            "Device image uploaded successfully:",
            imageResponse.data,
          );
        } catch (imageError) {
          console.error("Device image upload failed:", imageError);
          const imageErrorMsg =
            imageError.response?.data?.message ||
            imageError.response?.data?.error ||
            t("Image upload failed");
          setSubmitError(
            t(
              `Device ${
                isEditMode ? "updated" : "created"
              }, but image upload failed: ${imageErrorMsg}`,
            ),
          );
          setIsSubmitting(false);
          return;
        }
      }

      // Return the device ID for the photo manager to use
      setIsSubmitting(false);
      return currentDeviceId;
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} device:`,
        error,
      );

      // Extract specific error message from different possible response structures
      let errorMessage = t(
        `An error occurred while ${
          isEditMode ? "updating" : "creating"
        } the device. Please try again.`,
      );

      if (error.response?.data) {
        // Try different possible error message locations
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.errors) {
          // Handle array of errors
          if (Array.isArray(error.response.data.errors)) {
            errorMessage = error.response.data.errors.join(". ");
          } else if (typeof error.response.data.errors === "object") {
            errorMessage = Object.values(error.response.data.errors).join(". ");
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSubmitError(errorMessage);
      setIsSubmitting(false);
      throw error;
    }
  };

  const handleCancel = () => {
    // Clean up any blob URLs
    if (
      formData.deviceImagePreview &&
      formData.deviceImagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(formData.deviceImagePreview);
    }
    installedPhotos.forEach((photo) => {
      if (photo.preview && photo.preview.startsWith("blob:")) {
        URL.revokeObjectURL(photo.preview);
      }
    });
    if (embedded && onCancel) {
      onCancel();
      return;
    }

    navigate(-1);
  };

  const handleComplete = () => {
    setSubmitSuccess(
      t(`Device ${isEditMode ? "updated" : "created"} successfully!`),
    );
    console.log(
      `Device and photos ${isEditMode ? "updated" : "saved"} successfully`,
    );

    setTimeout(() => {
      if (embedded && onComplete) {
        onComplete();
        return;
      }

      navigate(-1);
    }, 2000);
  };

  const handleAddSpecification = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      specifications: [
        ...(prev.specifications || []),
        { id: `spec-${Date.now()}`, name: "", value: "" },
      ],
    }));
  }, []);

  const handleRemoveSpecification = useCallback((id) => {
    setFormData((prev) => ({
      ...prev,
      specifications:
        (prev.specifications || []).length > 1
          ? prev.specifications.filter((s) => s.id !== id)
          : [{ id: "spec-1", name: "", value: "" }],
    }));
  }, []);

  const handleSpecificationChange = useCallback((id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      specifications: (prev.specifications || []).map((s) =>
        s.id === id ? { ...s, [field]: value } : s,
      ),
    }));
  }, []);

  // Show loading state while fetching device data (edit mode only)
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Typography variant="h6">{t("Loading device data...")}</Typography>
      </Box>
    );
  }

  // Updated form validation logic - deviceId, status, and working condition are required
  const isFormValid =
    formData.deviceId?.trim() &&
    formData.status?.trim() &&
    formData.workingCondition?.trim() &&
    Object.keys(errors).length === 0;

  const RequiredLabel = ({ label }) => (
    <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
      <Typography variant="body2" component="span">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="span"
        color="error"
        sx={{ ml: 0.5 }}
      >
        *
      </Typography>
    </Box>
  );

  const StandardLabel = ({ label }) => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2">{label}</Typography>
    </Box>
  );

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;
  const orgId = JSON.parse(localStorage.getItem("userState"))?.organization?.id;

  return (
    <>
      {!embedded && (
        <SubTitle
          title={isEditMode ? "Update Device" : "Create Device"}
          path={`${orgName} > Devices `}
        />
      )}

      {submitSuccess && (
        <Alert severity="success" sx={{ mx: { xs: 2.5, md: 5 }, mb: 2 }}>
          {submitSuccess}
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" sx={{ mx: { xs: 2.5, md: 5 }, mb: 2 }}>
          {submitError}
        </Alert>
      )}

        <Box sx={{ display: "flex", flexDirection: "column" }}>
        <DeviceDetails
          formData={formData}
          handleInputChange={handleInputChange}
          handleImageUpload={handleImageUpload}
          handleRemoveImage={handleRemoveImage}
          RequiredLabel={RequiredLabel}
          StandardLabel={StandardLabel}
          errors={errors}
          handleAddSpecification={handleAddSpecification}
          handleRemoveSpecification={handleRemoveSpecification}
          handleSpecificationChange={handleSpecificationChange}
          compact={embedded}
          hideCloseButton={embedded && isEditMode}
          onClose={handleCancel}
          isEditMode={isEditMode}
          isReadOnly={isReadOnlyForOrgAdmin}
          disableInstallationStatus={isInstallationReadOnlyForOrgAdmin}
          hideIrisideaOwner={isOrganizationAdmin}
          lockOwnerToOrganization={lockOwnerToOrganization}
          fixedOwnerOrganizationName={currentOrganizationName}
          allowedCustomerOrganizationId={
            isOrganizationAdmin ? currentOrganizationId : null
          }
          /*
          Owned By "Other" / external vendor flow is disabled for create/update.
          forceOtherOwnerName={
            isOrganizationAdmin && !lockOwnerToOrganization
              ? "External Vendor"
              : null
          }
          */
          t={t}
        />
        {String(formData.deviceType || "")
          .toUpperCase()
          .replace(/[\s-]+/g, "_") !== "NFC_CARD" && (
          <SimDetails
            formData={formData}
            handleInputChange={handleInputChange}
            RequiredLabel={RequiredLabel}
            StandardLabel={StandardLabel}
            errors={errors}
            t={t}
            isEditMode={isEditMode}
          />
        )}

        {/* Only show installation-related sections once device is marked INSTALLED */}
        {formData.status === "INSTALLED" ? (
          <>
            <InstallationDetails
              formData={formData}
              handleInputChange={handleInputChange}
              RequiredLabel={RequiredLabel}
              StandardLabel={StandardLabel}
              errors={errors}
              t={t}
              selectedOrganization={selectedOrganization}
              setSelectedOrganization={setSelectedOrganization}
              isEditMode={isEditMode}
              disabled={isInstallationReadOnlyForOrgAdmin}
              lockOrganization={isOrganizationAdmin}
            />
            <ExecutiveDetails
              formData={formData}
              handleInputChange={handleInputChange}
              RequiredLabel={RequiredLabel}
              StandardLabel={StandardLabel}
              errors={errors}
              t={t}
              isEditMode={isEditMode}
              disabled={isInstallationReadOnlyForOrgAdmin}
            />
            <InstalledPhotos
              deviceId={isEditMode ? deviceId : null}
              photos={installedPhotos}
              setPhotos={setInstalledPhotos}
              isEditing={isEditMode}
              onComplete={handleComplete}
              orgId={orgId}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isDetailsFormValid={isFormValid}
              triggerFormValidation={validateForm}
              hideCancelButton
              showDeleteButton={
                embedded && isEditMode && !isReadOnlyForOrgAdmin
              }
              onDelete={onDelete}
              saveButtonText={isEditMode ? t("Update") : t("Save")}
              isEditMode={isEditMode}
              disabled={isReadOnlyForOrgAdmin}
              submitDisabled={isReadOnlyForOrgAdmin}
            />
          </>
        ) : (
          // Device not installed yet — no installation/executive/photos sections,
          // but we still need Save/Cancel actions so the form remains usable.
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              marginTop: "20px",
            }}
          >
            {!(embedded && isEditMode) && (
              <Button
                onClick={handleCancel}
                disabled={isSubmitting}
                sx={{
                  width: 120,
                  fontSize: "20px",
                  color: "#1F242E",
                  height: "55px",
                  borderRadius: "8px",
                  textTransform: "none",
                  background: "#E0E0E0",
                  border: "1px solid #414958",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#D0D0D0" },
                }}
              >
                {t("Cancel")}
              </Button>
            )}
            <Button
              onClick={async () => {
                const result = await handleSubmit();
                if (result !== undefined) {
                  handleComplete();
                }
              }}
              disabled={isSubmitting || !isFormValid || !canSubmitDevice}
              sx={{
                width: 120,
                height: "55px",
                fontSize: "20px",
                color: "#1F242E",
                textTransform: "none",
                borderRadius: "8px",
                background: "#F9E719",
                border: "1px solid #414958",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#f1de16ff" },
                "&:disabled": {
                  background: "#E0E0E0",
                  color: "#A0A0A0",
                  border: "1px solid #A0A0A0",
                },
              }}
            >
              {isSubmitting ? `${t("Saving")}...` : isEditMode ? t("Update") : t("Save")}
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ height: "40px" }}></Box>
    </>
  );
}
